import { useId, useState } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { Loader2 } from 'lucide-react';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { toast } from 'sonner';
import { env } from '../../lib/env';
import { forgotPasswordSchema, type ForgotPasswordFormData } from '../../lib/validation';
import { useScreenReaderAnnouncement } from '@/lib/accessibility';

export function ForgotPasswordForm() {
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSubmitted, setIsSubmitted] = useState(false);
  const announce = useScreenReaderAnnouncement();

  // Generate unique IDs for form elements
  const emailId = useId();
  const emailErrorId = useId();

  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm<ForgotPasswordFormData>({
    resolver: zodResolver(forgotPasswordSchema),
  });

  const onSubmit = async (data: ForgotPasswordFormData) => {
    setIsSubmitting(true);
    setIsSubmitted(false);

    try {
      const response = await fetch(`${env.apiBaseUrl}/auth/forgot-password`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(data),
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.detail || 'ForgotPassword failed');
      }

      setIsSubmitted(true);
    } catch (error) {
      if (error instanceof Error) {
        const errorMessage = error.message || 'ForgotPassword failed. Please check your credentials.';
        toast.error(errorMessage);
        announce(`ForgotPassword failed: ${errorMessage}`, 'assertive');
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <form onSubmit={handleSubmit(onSubmit)} className="space-y-6" noValidate>
      {isSubmitted && (
        <div
          className="rounded-lg border border-success-dark bg-success-light p-4"
          role="status"
        >
          <p className="font-body text-sm text-night-900">
            If you have a registered email in our system you will receive a password reset link
            from MyCareCal. <strong>Remember to check spam.</strong>
          </p>
        </div>
      )}

      {/* Email Field */}
      <div>
        <label htmlFor={emailId} className="block font-display text-sm font-semibold text-night-900 mb-2">
          Email Address
        </label>
        <Input
          id={emailId}
          type="email"
          autoComplete="email"
          placeholder="you@example.com"
          {...register('email')}
          aria-invalid={!!errors.email}
          aria-describedby={errors.email ? emailErrorId : undefined}
          aria-required={true}
          disabled={isSubmitting}
        />
        {errors.email && (
          <p id={emailErrorId} className="mt-1 text-sm text-error" role="alert">
            {errors.email.message}
          </p>
        )}
      </div>

      {/* Submit Button */}
      <Button
        type="submit"
        className="w-full"
        disabled={isSubmitting}
        size="lg"
        aria-describedby="forgotPassword-button-description"
      >
        {isSubmitting ? (
          <>
            <Loader2 className="mr-2 h-5 w-5 animate-spin" aria-hidden="true" />
            Resetting Password...
          </>
        ) : (
          'Reset Password'
        )}
      </Button>
      <span id="forgotPassword-button-description" className="sr-only">
        Submit the form to reset your password
      </span>
    </form>
  );
}
